import styled from 'styled-components'
import { colors, measurments } from '../utils/config'
import { MessageKey, message } from '../utils/messages'
import { Button, ResponsiveImage } from '@bm-js/atlascloud-ui'
import Textarea from './Textarea'
import Checkbox from './Checkbox'
import Select from './Select'
import {
  ChangeEventHandler,
  HTMLInputTypeAttribute,
  PropsWithChildren,
} from 'react'
import { ChangeEvent, FileEntryBM } from '@bm-js/atlascloud-shared'
import { useMedia } from '../utils/hooks'
import { captureException } from '@sentry/react'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'
import RichTextInput from './RichTextInput'
import { MediaSelector } from '../contexts/Media.context'

const StyledInput = styled.div<{ $hasNoMargin?: boolean }>`
  label,
  span,
  input {
    display: block;
  }
  label {
    font-family: 'Inter', sans-serif;
    font-size: 1rem;
    line-height: 1.5rem;
    color: ${colors.gray700};
    font-weight: 500;
  }
  span {
    font-family: 'Inter', sans-serif;
    font-size: 0.75rem;
    line-height: 1rem;
    color: ${colors.gray600};
    margin-bottom: 0.4rem;
  }
  input {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid ${colors.gray500};
    border-radius: ${measurments.borderRadius}px;
    font-size: 0.875rem;
    color: ${colors.gray700};
    padding: 0.6rem;
    &[type='color'] {
      padding: 0;
      border: none;
      background: none;
      border-radius: ${measurments.borderRadius}px;
      overflow: hidden;
    }
  }
  margin-bottom: ${({ $hasNoMargin }) => ($hasNoMargin ? '0' : '1.6rem')};
  &.light {
    label {
      font-size: 0.875rem;
      line-height: 1.25rem;
      font-weight: 400;
      color: ${colors.gray700};
    }
    span {
      font-size: 0.75rem;
      line-height: 1rem;
      font-weight: 400;
      color: ${colors.gray600};
      margin-bottom: 0.25rem;
    }
    input {
      font-size: 0.875rem;
      padding: 0.4rem;
    }
  }
`

interface Props
  extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  setter?: (f: FileEntryBM) => void
  light?: boolean
  error?: string
  noMargin?: boolean
  label?: string
  type?: HTMLInputTypeAttribute | 'rich-text' | 'select' | 'textarea' | 'media'
  desc?: string
  onChange?: (e: ChangeEvent) => void
  deleteMedia?: () => void
  moveMedia?: (diff: number) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any
  options?: { value: string; label: string }[]
}

const Input = (props: PropsWithChildren<Props>) => {
  const { type } = props
  const {
    setter,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    children: _children,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    light: _light,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    error: _error,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    noMargin: _noMargin,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    label: _label,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    desc: _desc,
    deleteMedia,
    moveMedia,
    ...inputProps
  } = props

  const mediaInputProps = {
    ...props,
    setter,
    moveMedia,
    deleteMedia,
  }

  if (type === 'image') {
    return (
      <InputWrapper {...props}>
        <ImageInput {...mediaInputProps} />
      </InputWrapper>
    )
  }

  if (type === 'media') {
    return (
      <InputWrapper {...props}>
        <MediaInput
          {...mediaInputProps}
          fileType={undefined}
          trKeys={{
            select: TrKey.mediaInputSelectMedia,
            change: TrKey.mediaInputChangeMedia,
            remove: TrKey.mediaInputRemoveMedia,
          }}
        />
      </InputWrapper>
    )
  }

  if (type === 'textarea') {
    return (
      <InputWrapper {...props}>
        <Textarea {...inputProps} />
      </InputWrapper>
    )
  }

  if (type === 'checkbox') {
    return <CheckboxWrapper {...props} />
  }

  if (type === 'select') {
    return <SelectWrapper {...props} />
  }

  if (type === 'rich-text') {
    return (
      <InputWrapper {...props} className={`${props.className} mb-4`}>
        <RichTextInput {...inputProps} />
      </InputWrapper>
    )
  }

  return (
    <InputWrapper {...props}>
      <input {...inputProps} />
    </InputWrapper>
  )
}

const InputWrapper = (props: Props) => {
  const { children, light, error, noMargin, label, desc, className } = props
  return (
    <StyledInput
      $hasNoMargin={noMargin}
      className={`${light ? 'light' : ''} ${className}`}
    >
      {label && <label htmlFor={props.id}>{label}</label>}
      {desc && <span>{desc}</span>}
      {children}
      {error && (
        <p className='text-xs text-red-500'>{message(error as MessageKey)}</p>
      )}
    </StyledInput>
  )
}

const ImageInput = (props: Props) => {
  return (
    <MediaInput
      {...props}
      fileType='image'
      trKeys={{
        select: TrKey.imageInputSelectImage,
        change: TrKey.imageInputChangeImage,
        remove: TrKey.imageInputRemoveImage,
      }}
    />
  )
}

const MediaInput = (
  props: Props & {
    fileType: MediaSelector['fileType']
    trKeys: {
      select: TrKey
      change: TrKey
      remove: TrKey
    }
  }
) => {
  const { setter, value, deleteMedia, moveMedia, fileType, trKeys } = props
  const { tr } = useTr()

  const { openMediaSelector, closeMediaSelector } = useMedia()

  if (!setter) {
    captureException('setter is required for media input', {
      extra: {
        props,
      },
    })
  }

  const handleAddMedia = () => {
    openMediaSelector({
      fileType,
      onSelect: (file) => {
        if (file) setter?.(file)
        closeMediaSelector()
      },
    })
  }

  const _value = value as FileEntryBM | undefined

  return (
    <div className='flex flex-col items-end'>
      {fileType === 'image' || _value?.contentType.startsWith('image/') ? (
        <ResponsiveImage file={_value} />
      ) : (
        _value && (
          <div className='flex items-center gap-2 border border-gray-300 rounded-md p-2 text-sm min-w-[200px] text-right'>
            <p>{_value?.name}</p>
            <p>{_value?.contentType}</p>
          </div>
        )
      )}
      <div className='mt-2 flex items-center gap-1'>
        {!!moveMedia && (
          <>
            <Button sm onClick={() => moveMedia(-1)}>
              ↑
            </Button>
            <Button sm onClick={() => moveMedia(1)}>
              ↓
            </Button>
          </>
        )}
        <Button sm={!!moveMedia} onClick={handleAddMedia}>
          {_value?.name ? tr(trKeys.change) : tr(trKeys.select)}
        </Button>
        {!!deleteMedia && (
          <Button sm={!!moveMedia} danger onClick={deleteMedia}>
            {tr(trKeys.remove)}
          </Button>
        )}
      </div>
    </div>
  )
}

const CheckboxWrapper = (props: Props) => {
  const { value, onChange, ...checkboxProps } = props

  const _onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const event = {
      target: {
        name: e.target.name,
        value: e.target.checked,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>
    onChange?.(event)
  }

  return (
    <Checkbox
      {...checkboxProps}
      checked={value as unknown as boolean}
      onChange={_onChange}
    />
  )
}

const SelectWrapper = (props: Props) => {
  const { options, ...selectProps } = props

  return (
    <Select {...(selectProps as React.InputHTMLAttributes<HTMLSelectElement>)}>
      {options!.map((option, i) => (
        <option
          key={`select_leftbar_${i}_${props.name}_${props.id}`}
          value={option.value}
        >
          {option.label}
        </option>
      ))}
    </Select>
  )
}

export default Input
